import React, { useEffect, useRef, useState } from 'react';

/**
 * ScrollReveal - wraps children with a fade-in + slide-up animation
 * triggered when the element enters the viewport.
 *
 * Props:
 *   delay     - delay in ms before animation starts (default: 0)
 *   direction - 'up' | 'left' | 'right' (default: 'up')
 *   className - extra CSS classes to pass through
 */
const ScrollReveal = ({ children, delay = 0, direction = 'up', className = '' }) => {
    const [isVisible, setIsVisible] = useState(false);
    const domRef = useRef();

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                        observer.unobserve(entry.target); // Ne se rejoue pas
                    }
                });
            },
            { threshold: 0.12 }
        );
        const current = domRef.current;
        if (current) observer.observe(current);
        return () => { if (current) observer.unobserve(current); };
    }, []);

    const directionStyles = {
        up:    'translateY(40px)',
        left:  'translateX(-40px)',
        right: 'translateX(40px)',
    };

    const style = {
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'none' : (directionStyles[direction] || directionStyles.up),
        transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
        willChange: 'opacity, transform',
    };

    return (
        <div ref={domRef} style={style} className={className}>
            {children}
        </div>
    );
};

export default ScrollReveal;
